import { imgUrl, thumbnail } from '../../../../utils/images';
import Datepicker, { DateType } from 'react-tailwindcss-datepicker';
import { Input } from '../../../../components/inputs/input';
import { TextArea } from '../../../../components/inputs/textarea/textarea';
import React, { useRef, useState } from 'react';
import { FileUploadResult, useUploadFiles } from '../../../../utils/upload';
import path from 'path-browserify';
import { InfoAlert } from '../../../../components/alerts/info';
import { SuccessAlert } from '../../../../components/alerts/success';
import { ErrorAlert } from '../../../../components/alerts/error';
import { parseISO } from 'date-fns';
import {
  TripResponse,
  useDeleteTripsByTripIdMutation,
  usePatchTripsByTripIdMutation,
} from '../../../../store/backendApi';
import { useNavigate } from 'react-router-dom';

export interface HeaderOptionsPageProps {
  tripData: TripResponse;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

const dateStr = (d: Date | undefined | null) => {
  return d?.toLocaleDateString('sv-SE') ?? undefined;
};

export const HeaderOptionsPage = (props: HeaderOptionsPageProps) => {
  const { tripData: trip } = props;
  const { uploadFiles } = useUploadFiles();
  const [changeHeaderSuccessful, setChangeHeaderSuccessful] = useState(false);
  const [changeHeaderFailed, setChangeHeaderFailed] = useState(false);
  const [changeHeaderLoading, setChangeHeaderLoading] = useState(false);

  const [tripName, setTripName] = useState<string>(props.tripData.title);
  const [tripDescription, setTripDescription] = useState<string>(props.tripData.description);
  const [tripHeader, setTripHeader] = useState<string>(props.tripData.headerFileName);
  const [tripDates, setTripDates] = useState<{ startDate: DateType; endDate: DateType } | null>(
    props.tripData.startDate && props.tripData.endDate
      ? { startDate: parseISO(props.tripData.startDate), endDate: parseISO(props.tripData.endDate) }
      : null,
  );
  const [patchTrip, { error: patchTripError, isLoading: patchTripLoading }] =
    usePatchTripsByTripIdMutation();
  const [deleteTrip, { error: deleteTripError, isLoading: deleteTripLoading }] =
    useDeleteTripsByTripIdMutation();
  const navigate = useNavigate();
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasChanges =
    tripName !== trip.title ||
    tripDescription !== trip.description ||
    tripHeader !== trip.headerFileName ||
    dateStr(tripDates?.endDate) !== trip.endDate ||
    dateStr(tripDates?.startDate) !== trip.startDate;

  const updateTrip = async () => {
    await patchTrip({
      tripId: props.tripData.id,
      partialTripBodyRequest: {
        title: tripName ? tripName : undefined,
        description: tripDescription ? tripDescription : undefined,
        startDate: tripDates ? dateStr(tripDates.startDate) : undefined,
        endDate: tripDates ? dateStr(tripDates.endDate) : undefined,
        headerFileName: tripHeader ? tripHeader : undefined,
      },
    });
    if (patchTripError) {
      console.error(patchTripError);
      alert('Trip could not be updated');
    } else {
      props.setHasChanges(true);
    }
  };

  const reset = () => {
    setTripName(props.tripData.title);
    setTripDescription(props.tripData.description);
    setTripDates(
      props.tripData.startDate && props.tripData.endDate
        ? {
          startDate: parseISO(props.tripData.startDate),
          endDate: parseISO(props.tripData.endDate),
        }
        : null,
    );
  };

  const handleDelete = async () => {
    if (!deleteConfirmation) {
      setDeleteConfirmation(true);
      return;
    }

    try {
      await deleteTrip(props.tripData.id).unwrap();
      navigate('/');
    } catch (error) {
      console.error(deleteTripError);
      alert('Trip could not be deleted');
    }
  };

  const onFileUploaded = async (fileUploadResult: FileUploadResult) => {
    if (!fileUploadResult.success || !fileUploadResult.fileUpload) {
      console.error(fileUploadResult.error);
      showFailure();
      return;
    }
    const headerUrl = fileUploadResult.fileUpload.uploadUrl.pathname;
    setTripHeader(path.basename(headerUrl));
    showSuccess();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setChangeHeaderLoading(true);
    await uploadFiles({
      tripId: props.tripData.id,
      files: [file],
      onUploaded: onFileUploaded,
    });
  };

  const showSuccess = () => {
    setChangeHeaderLoading(false);
    setChangeHeaderSuccessful(true);
    setTimeout(() => {
      setChangeHeaderSuccessful(false);
    }, 2000);
  };

  const showFailure = () => {
    setChangeHeaderLoading(false);
    setChangeHeaderFailed(true);
    setTimeout(() => {
      setChangeHeaderFailed(false);
    }, 2000);
  };
  return (
    <>
      <p className="mt-4">General trip information</p>
      {changeHeaderLoading && <InfoAlert text="Uploading..." />}
      {changeHeaderSuccessful && <SuccessAlert text="Header updated!" />}
      {changeHeaderFailed && <ErrorAlert text="Header upload failed" />}
      <div>
        <div className="w-4/5 rounded-lg aspect-[3/1] m-auto mt-6 bg-transparent">
          <div className="relative rounded-lg group h-full w-full">
            <img
              ref={imgRef}
              id="header-image"
              alt=""
              src={
                tripHeader
                  ? imgUrl(props.tripData.id, thumbnail(tripHeader))
                  : '/default-trip-header.jpg'
              }
              className="object-cover w-full h-full m-auto rounded-lg"
            />
            <div
              className="absolute rounded-lg inset-0 bg-gray-800 bg-opacity-50 flex items-center
                justify-center text-white text-lg font-semibold opacity-80 group-hover:opacity-100
                transition-opacity cursor-pointer text-center"
              onClick={() => fileInputRef.current?.click()}
            >
              Change Picture
            </div>
          </div>
        </div>
        <div className="max-w-[70%] sm:max-w-none m-auto">
          <div className="mt-6">
            <Datepicker
              inputId={'trip-dates'}
              value={tripDates}
              onChange={(newValue) => setTripDates(newValue)}
            />
          </div>
          <div className="mt-6">
            <Input
              id={'trip-name'}
              name={'trip name'}
              type={'text'}
              placeholder={'Title'}
              value={tripName}
              setValue={setTripName}
            />
          </div>
          <div className="mt-6">
            <TextArea
              id={'trip-desc'}
              name={'trip description'}
              rows={2}
              placeholder={'Description'}
              value={tripDescription}
              setValue={setTripDescription}
            />
          </div>
        </div>
      </div>
      <hr className="mt-12" />
      <div className="mt-5 sm:mt-6">
        <div className="flex gap-3">
          <button
            type="button"
            disabled={patchTripLoading || !hasChanges}
            onClick={() => {
              updateTrip();
            }}
            className="flex-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm disabled:text-gray-300
                font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600
                disabled:bg-gray-500 disabled:cursor-not-allowed disabled:hover:bg-gray-500"
          >
            {patchTripLoading ? '...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={reset}
            className="flex-1 justify-center rounded-md bg-gray-300 disabled:bg-gray-500 disabled:hover:bg-gray-500
             px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-400 focus-visible:outline
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
          >
            Reset
          </button>
        </div>
        <div className="mt-3">
          <button
            type="button"
            disabled={deleteTripLoading}
            onClick={handleDelete}
            className="w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm
                font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
          >
            {deleteTripLoading ? '...' : deleteConfirmation ? 'Confirm delete?' : 'Delete Trip'}
          </button>
        </div>
      </div>
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </>
  );
};
